import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/capitalStockIssueAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCapitalCCList = createAsyncThunk(
    'capitalStockIssue/fetchCapitalCCList',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getCapitalissueccs(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchCapitalItemCodes = createAsyncThunk(
    'capitalStockIssue/fetchCapitalItemCodes',
    async ({ uid, rid, ccCode }, { rejectWithValue }) => {
        try {
            const res = await api.getCapitalItemCodes(uid, rid, ccCode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load master item codes'));
        }
    }
);

export const fetchCapitalStockGrid = createAsyncThunk(
    'capitalStockIssue/fetchCapitalStockGrid',
    async ({ masterItemCode, ccCode }, { rejectWithValue }) => {
        try {
            const res = await api.getCapitalStockIssueDatatable(masterItemCode, ccCode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load stock conversion items'));
        }
    }
);

// Success format from the API is "Submited,<TransactionId>" — anything else is an
// error message meant to be shown verbatim.
export const submitCapitalStockIssue = createAsyncThunk(
    'capitalStockIssue/submitCapitalStockIssue',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveCapitalItemsIssue(payload);
            const raw = res?.Data || '';
            const [status, code] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit capital stock issue');
            }
            return { code };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit capital stock issue'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    itemCodeList: [],
    gridItems: [],
    submitResult: null,
    loading: {
        ccList: false,
        itemCodeList: false,
        grid: false,
        submit: false,
    },
    errors: {
        ccList: null,
        itemCodeList: null,
        grid: null,
        submit: null,
    },
};

const capitalStockIssueSlice = createSlice({
    name: 'capitalStockIssue',
    initialState,
    reducers: {
        clearItemCodeList: (s) => { s.itemCodeList = []; },
        clearGrid: (s) => { s.gridItems = []; s.errors.grid = null; },
        clearSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetCapitalStockIssue: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCapitalCCList.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCapitalCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCapitalCCList.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchCapitalItemCodes.pending,   (s) => { s.loading.itemCodeList = true; s.errors.itemCodeList = null; })
            .addCase(fetchCapitalItemCodes.fulfilled, (s, a) => { s.loading.itemCodeList = false; s.itemCodeList = a.payload; })
            .addCase(fetchCapitalItemCodes.rejected,  (s, a) => { s.loading.itemCodeList = false; s.errors.itemCodeList = a.payload; s.itemCodeList = []; });

        builder
            .addCase(fetchCapitalStockGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchCapitalStockGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchCapitalStockGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; s.gridItems = []; });

        builder
            .addCase(submitCapitalStockIssue.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitCapitalStockIssue.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridItems = []; })
            .addCase(submitCapitalStockIssue.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearItemCodeList,
    clearGrid,
    clearSubmitResult,
    resetCapitalStockIssue,
} = capitalStockIssueSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectCSICCList       = (s) => s.capitalStockIssueCreation.ccList;
export const selectCSIItemCodeList = (s) => s.capitalStockIssueCreation.itemCodeList;
export const selectCSIGridItems    = (s) => s.capitalStockIssueCreation.gridItems;
export const selectCSISubmitResult = (s) => s.capitalStockIssueCreation.submitResult;
export const selectCSILoading      = (s) => s.capitalStockIssueCreation.loading;
export const selectCSIErrors       = (s) => s.capitalStockIssueCreation.errors;

export default capitalStockIssueSlice.reducer;
